import { dehydrate, QueryClient } from '@tanstack/react-query'
import omitEmptyOrNil from '@vangst/lib/omitEmptyOrNil'
import shuffle from '@vangst/lib/shuffle'
import { prefetchBlogPosts } from '@vangst/services/graphcms/marketing/useBlogPosts'
import { Tags } from '@vangst/services/graphcms/types'
import HttpError from '@vangst/services/HttpError'
import { CompanyFragment } from '@vangst/services/oogst/company/useCompany'
import { prefetchElasticSearch } from '@vangst/services/oogst/search/useElasticSearch'
import { RobotsEnum } from '@vangst/services/oogst/types'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import Routes from '@vangst/services/routes'
import { InferGetStaticPropsType } from 'next'
import Busy from '../../components/feedback/Busy'
import Breadcrumbs from '../../components/navigation/Breadcrumbs'
import Meta from '../../domains/application/Meta'
import CompanyDashboardSections from '../../domains/company/CompanyDashboardSections'
import MemberDashboardSections from '../../domains/member/MemberDashboardSections'
import InternalUserDashboardSections from '../../domains/recruiter/InternalUserDashboardSections'
import DashboardHeader from '../../domains/viewer/DashboardHeader'

export async function getStaticProps() {
  const queryClient = new QueryClient()

  const blogVars = {
    tags_contains_all: [Tags.CannabisHiring],
    tags_contains_none: [],
    searchTerm: '',
  }
  const blogPosts = await prefetchBlogPosts(queryClient, blogVars)
  const learn = blogPosts ? shuffle(blogPosts).slice(0, 3) : undefined

  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: null, text: 'Dashboard' },
  ]

  const companiez = await prefetchElasticSearch(queryClient, 'searchClients', {
    first: 24,
  })

  const nodes = companiez?.nodes as CompanyFragment[] | undefined

  const companies = (nodes ? shuffle(nodes) : nodes)?.slice(0, 3)

  return {
    props: {
      companies: companies || null,
      crumbs,
      dehydratedState: omitEmptyOrNil(dehydrate(queryClient), true),
      learn,
    },
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function DashboardPage(props: PropsType) {
  const { crumbs } = props
  const { authenticating, viewer } = useViewer()

  const unauthorized = !authenticating && viewer == null

  if (unauthorized) {
    throw new HttpError(401)
  }

  // TODO: We can untangle these back into the view soon
  // Just needed a minute to get my bearings/definitions localized
  const userScope = {
    isBreathing: viewer != null,
    isInternalUser: viewer?.permissions?.internalUser,
    isCompany: !!viewer?.client,
    isMember:
      viewer?.permissions?.updateConnectionStatus &&
      !viewer?.permissions?.searchCandidatesAsClient,
  }

  const { isBreathing, isInternalUser, isCompany, isMember } = userScope

  return (
    <>
      <Meta
        key="meta"
        crumbs={crumbs}
        title="Vangst | Dashboard"
        robots={RobotsEnum.NoindexNofollow}
      />
      <Breadcrumbs crumbs={crumbs} />
      {authenticating && (
        <section className="contain-xyc">
          <Busy />
        </section>
      )}
      {isBreathing && (
        <>
          <DashboardHeader userScope={userScope} />

          <div className="contain-c mood-white">
            <article className="contain-xy flow-y-md">
              {isInternalUser && <InternalUserDashboardSections />}

              {isCompany && <CompanyDashboardSections />}

              {isMember && !isCompany && <MemberDashboardSections />}
            </article>
          </div>
        </>
      )}
    </>
  )
}
